import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import Box from '../../elements/Box';
import Col from '../../elements/Col';
import Dot from './atoms/Dot';
import ActionButton from './atoms/ActionButton';

/**
 * This hook tracks if the carousel is dragging and prevents that a
 * dragging over an item to be captured as a click and trigger the
 * corresponding action. This is an know issue of slick carousel and
 * this is a common workaround. The issue happens for the dragging behavior
 * using mouse events, pointer events on mobile work just fine without this.
 * See for more context: https://github.com/akiran/react-slick/issues/604
 */
function useCarouselDragging() {
  const [dragging, setDragging] = useState(false);

  const handleBeforeChange = useCallback(() => {
    setDragging(true);
  }, [setDragging]);

  const handleAfterChange = useCallback(() => {
    setDragging(false);
  }, [setDragging]);

  const handleOnItemClick = useCallback(
    (e) => {
      if (dragging) {
        e.stopPropagation();
      }
    },
    [dragging]
  );

  return {
    handleBeforeChange,
    handleAfterChange,
    handleOnItemClick
  };
}

// Apply settings for current active dot
const activeDotSettings = (index, dotActive, dotSettings) =>
  index === dotActive ? dotSettings.dotActiveSetting : {};

// Length of dots array
const dotsLength = ({ length, elementsToShow, elementsToScroll }) => {
  const coef = (length - elementsToShow) % elementsToScroll === 0 ? 1 : 2;
  return Math.trunc((length - elementsToShow) / elementsToScroll) + coef;
};

// Apply setting for dots in each side of the carousel
const sideDotsSettings = (
  i,
  dotsVisible,
  dotSettings,
  direction,
  itemsData,
  elementsToShow,
  elementsToScroll
) => {
  const { from, to } = dotsVisible;
  const length = dotsLength({ length: itemsData.length, elementsToShow, elementsToScroll });
  const startPoint = direction === 'ltr' ? 0 : length - 1;
  const endPoint = direction === 'ltr' ? length - 1 : 0;
  return (i === from && i !== startPoint) || (i === to && i !== endPoint)
    ? dotSettings.sideDots
    : {};
};

const getElementsToShow = ({ widthCardAuto, itemsData, elementsToShow }) =>
  widthCardAuto && itemsData.length < elementsToShow ? itemsData.length : elementsToShow;

// Hooks for manage element to show
const useElementsToShow = ({ widthCardAuto, itemsData, elementsToShow }) => {
  return React.useState(getElementsToShow({ widthCardAuto, itemsData, elementsToShow }));
};

const getElementsToScroll = ({ widthCardAuto, itemsData, elementsToScroll }) =>
  widthCardAuto && itemsData.length < elementsToScroll ? itemsData.length : elementsToScroll;

// Hooks for manage element to scroll
const useElementsToScroll = ({ widthCardAuto, itemsData, elementsToScroll }) =>
  React.useState(getElementsToScroll({ widthCardAuto, itemsData, elementsToScroll }));

// Hook for work with dots
const useDots = ({ itemsData, elementsToShow, elementsToScroll, maxNumberDots }) => {
  const { direction } = React.useContext(Context);
  const dotsActiveFrom = dotsLength({ length: itemsData.length, elementsToShow, elementsToScroll });
  const dotsActiveTo = dotsActiveFrom - maxNumberDots < 0 ? 0 : dotsActiveFrom - maxNumberDots;
  const [dotActive, setDotActive] = React.useState(direction === 'ltr' ? 0 : dotsActiveFrom - 1);
  const [dotsVisible, setDotsVisible] = React.useState({
    from: direction === 'ltr' ? 0 : dotsActiveFrom - 1,
    to: direction === 'ltr' ? maxNumberDots - 1 : dotsActiveTo
  });
  return [dotActive, dotsVisible, setDotActive, setDotsVisible];
};

// Define if dot is visible
const isDotVisible = (i, dotsVisible, direction) => {
  let result = false;
  if (direction === 'ltr') {
    result = i >= dotsVisible.from && i <= dotsVisible.to;
  } else {
    result = i <= dotsVisible.from && i >= dotsVisible.to;
  }
  return result;
};

// Translate list to not have blank spaces
const translateElements = ({ itemsData, elementsToShow, slickCarouselContainerRef }) => {
  if (slickCarouselContainerRef.current) {
    const card = slickCarouselContainerRef.current.querySelector('.slick-slide');
    if (card && card.style) {
      const width = (itemsData.length - elementsToShow) * card.offsetWidth;
      const element = slickCarouselContainerRef.current.querySelector('.slick-track');
      if (element) {
        element.style.transform = `translate3d(-${width}px, 0px, 0px)`;
      }
    }
  }
};

const getNewIndex = ({ index, elementsToShow, itemsData, direction }) => {
  let newIndex = Math.trunc(index);
  if (direction === 'ltr' && newIndex + Math.trunc(elementsToShow) > itemsData.length - 1) {
    newIndex = itemsData.length - Math.trunc(elementsToShow);
  }
  if (direction === 'rtl') {
    newIndex = itemsData.length - index - elementsToShow;
    newIndex = newIndex < 0 ? 0 : newIndex;
  }

  return newIndex;
};

const Carousel = (props) => {
  const {
    showDots,
    showNavigation,
    autoplay,
    infinite,
    scaleCards,
    pauseOnDotsHover,
    pauseOnFocus,
    pauseOnHover,
    speed,
    itemsData,
    lazyLoad,
    prevButtonSettings,
    nextButtonSettings,
    reelSettings,
    dotSettings,
    colSettings,
    itemSettings,
    ItemComponentInstance,
    maxNumberDots,
    showArrowsWhenDisabled,
    afterChange,
    cardHeight,
    beforeChange,
    tracking
  } = props;

  const {
    // eslint-disable-next-line no-unused-vars
    handleAfterChange: handleDraggingAfterChange,
    // eslint-disable-next-line no-unused-vars
    handleBeforeChange: handleDraggingBeforeChange,
    handleOnItemClick
  } = useCarouselDragging();
  const { direction, theme } = React.useContext(Context);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [currentBtnActive, setCurrentBtnActive] = React.useState('next');
  const [elementsToShow, setElementsToShow] = useElementsToShow(props);
  const [elementsToScroll, setElementsToScroll] = useElementsToScroll(props);
  const [dotActive, dotsVisible, setDotActive, setDotsVisible] = useDots({
    itemsData,
    elementsToShow,
    elementsToScroll,
    maxNumberDots
  });

  const newProps = {
    ...props,
    'cmp-styles': {
      '--point--slick-track': {
        display: 'flex'
      },
      '--point--slick-track--space----point--slick-slide': {
        height: 'unset !important',
        float: 'none'
      },
      ...props['cmp-styles']
    }
  };

  const [className, style] = useStyles(newProps, { direction, theme });
  const slickCarouselContainerRef = React.useRef(null);
  const slickCarouselRef = React.useRef(null);
  const arrayRef = React.useRef([]);
  const isKeyboardNavigator = React.useRef(false);
  const { translate } = React.useContext(Context);

  const emptyCards =
    Number.isInteger(elementsToShow) && itemsData.length % elementsToShow !== 0
      ? Math.floor(elementsToShow - (itemsData.length % elementsToShow))
      : 0;
  useMemo(() => {
    if (emptyCards && itemsData.length > props.elementsToShow) {
      Array(emptyCards)
        .fill()
        .forEach(() => {
          itemsData.push({ noData: true, data: [] });
        });
    }
  }, [emptyCards, itemsData, props.elementsToShow]);

  const itemsSize = itemsData.length;
  const prevDisabled = currentIndex === 0;
  const nextDisabled = currentIndex >= itemsSize - elementsToShow;

  const removeAccessibilityRulesBase = () => {
    if (slickCarouselContainerRef.current) {
      [].slice
        .call(slickCarouselContainerRef.current.querySelectorAll('.slick-slide'))
        .forEach((item) => {
          item.removeAttribute('tabindex');
          item.removeAttribute('aria-hidden');
        });
    }
  };

  React.useEffect(() => {
    removeAccessibilityRulesBase();
  }, []);

  // Update carousel position when data changed
  React.useEffect(() => {
    if (elementsToShow >= props.elementsToShow) {
      slickCarouselRef.current.slickGoTo(
        direction === 'rtl' ? itemsData.length - elementsToShow : 0
      );
    }
    if (direction === 'ltr') {
      setCurrentIndex(0);
      setDotActive(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, itemsData]);

  // When change currentIndex set focus in firs card visible
  React.useEffect(() => {
    if (isKeyboardNavigator.current && arrayRef.current[currentIndex]) {
      if (
        (currentBtnActive === 'prev' && direction === 'ltr') ||
        (currentBtnActive === 'next' && direction === 'rtl')
      ) {
        arrayRef.current[currentIndex + elementsToShow - 1].focus();
      } else {
        arrayRef.current[currentIndex].focus();
      }
      isKeyboardNavigator.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrayRef, currentIndex]);

  // Recalculate elements when data change
  React.useEffect(() => {
    setElementsToShow(getElementsToShow(props));
    setElementsToScroll(getElementsToScroll(props));
  }, [itemsData, props, setElementsToScroll, setElementsToShow]);

  // Translate the last element to not have blank spaces
  React.useEffect(() => {
    if (
      dotActive + 1 ===
      dotsLength({ length: itemsData.length, elementsToShow, elementsToScroll })
    ) {
      translateElements({ elementsToShow, itemsData, slickCarouselContainerRef });
    }
    // Eslint disabled for itemsData is not necessary, because monthly histogram change itemsData before carousel setDotIndex.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotActive, elementsToScroll, elementsToShow]);

  // Execute before change slick page
  const handleBeforeChange = (oldIndex, index) => {
    // handleDraggingBeforeChange();
    const newIndex = getNewIndex({ direction, index, elementsToShow, itemsData });
    beforeChange(oldIndex, newIndex);
  };

  // Execute in before change for update dots and current slide
  const handleChange = (index) => {
    // handleDraggingAfterChange();
    const newIndex = getNewIndex({ direction, index, elementsToShow, itemsData });
    if (currentIndex !== newIndex) {
      const { from, to } = dotsVisible;
      const newRange = { ...dotsVisible };
      let newDotActive = 0;
      if (index !== 0) {
        newDotActive = index / elementsToScroll;
      }
      const length = dotsLength({ length: itemsData.length, elementsToShow, elementsToScroll });
      const startPoint = direction === 'ltr' ? from : to;
      const endPoint = direction === 'ltr' ? to : from;
      if (
        newIndex > currentIndex &&
        ((newDotActive === endPoint && newDotActive !== length - 1 && direction === 'ltr') ||
          (newDotActive === startPoint && newDotActive !== 0 && direction === 'rtl'))
      ) {
        newRange.from = direction === 'ltr' ? from + 1 : from - 1;
        newRange.to = direction === 'ltr' ? to + 1 : to - 1;
      } else if (
        newIndex < currentIndex &&
        ((newDotActive === startPoint && newDotActive !== 0 && direction === 'ltr') ||
          (newDotActive === endPoint && newDotActive !== length - 1 && direction === 'rtl'))
      ) {
        newRange.from = direction === 'ltr' ? from - 1 : from + 1;
        newRange.to = direction === 'ltr' ? to - 1 : to + 1;
      }
      setDotActive(newDotActive);
      setDotsVisible(newRange);
      setCurrentIndex(newIndex);
      afterChange(newIndex);
    }
    removeAccessibilityRulesBase();
  };

  // Define if user is navigate by keyboard
  const activeKeyboard = () => {
    isKeyboardNavigator.current = true;
  };

  // Go to next slide and if rtl go to back slide
  const handleNext = (e, withKeyboard) => {
    if (slickCarouselRef.current) {
      if (direction === 'ltr') {
        setCurrentBtnActive('next');
        slickCarouselRef.current.slickNext();
      } else {
        setCurrentBtnActive('prev');
        slickCarouselRef.current.slickPrev();
      }
      tracking({ type: 'CAROUSEL_ARROW_RIGHT' });
      if (withKeyboard) {
        activeKeyboard();
      }
    }
  };

  // Go to back slide and if rtl go to next slide
  const handlePrev = (e, withKeyboard) => {
    if (slickCarouselRef.current) {
      if (direction === 'ltr') {
        setCurrentBtnActive('prev');
        slickCarouselRef.current.slickPrev();
      } else {
        setCurrentBtnActive('next');
        slickCarouselRef.current.slickNext();
      }
      tracking({ type: 'CAROUSEL_ARROW_LEFT' });
      if (withKeyboard) {
        activeKeyboard();
      }
    }
  };

  // Calculate height of the cards, prev and next buttons
  const newHeight = cardHeight || '100%';
  const buttonsHeight = { height: newHeight };
  itemSettings['cmp-styles'] = { ...itemSettings['cmp-styles'], height: newHeight };

  // Define slick settings
  const settings = {
    rtl: direction === 'rtl' ? true : false,
    dots: showDots,
    arrows: showNavigation,
    infinite,
    className: scaleCards ? 'fr-p-scale-cards' : '',
    slidesToShow: elementsToShow,
    slidesToScroll: elementsToScroll,
    autoplay,
    pauseOnDotsHover,
    pauseOnFocus,
    pauseOnHover,
    speed,
    initialSlide: direction === 'rtl' ? itemsData.length - 1 - (elementsToShow - 1) : 0,
    afterChange: handleChange,
    beforeChange: handleBeforeChange,
    lazyLoad: lazyLoad === 'disabled' ? null : lazyLoad,
    prevArrow: (
      <ActionButton
        buttonsHeight={buttonsHeight}
        showArrowsWhenDisabled={showArrowsWhenDisabled}
        disabled={prevDisabled}
        settings={prevButtonSettings}
        labelKey="cmp-carrousel-prev"
        left="0"
        onArrowClick={handlePrev}
        dataTestId="carousellBtnPrevious"
      />
    ),
    nextArrow: (
      <ActionButton
        buttonsHeight={buttonsHeight}
        showArrowsWhenDisabled={showArrowsWhenDisabled}
        disabled={nextDisabled}
        settings={nextButtonSettings}
        labelKey="cmp-carrousel-next"
        right="0"
        onArrowClick={handleNext}
        dataTestId="carousellBtnNext"
      />
    ),
    // eslint-disable-next-line react/display-name
    appendDots: (dots) => (
      <Box {...reelSettings}>
        <ul>{dots}</ul>
      </Box>
    ),
    // eslint-disable-next-line react/display-name
    customPaging: (i) => {
      return (
        <Dot
          {...dotSettings}
          {...activeDotSettings(i, dotActive, dotSettings)}
          {...sideDotsSettings(
            i,
            dotsVisible,
            dotSettings,
            direction,
            itemsData,
            elementsToShow,
            elementsToScroll
          )}
          pageNumber={i}
          isDotVisible={isDotVisible(i, dotsVisible, direction)}
        />
      );
    }
  };

  const componentClass = `${Carousel.componentName} ${
    showDots && itemsSize > elementsToShow ? 'fr-show-dots' : ''
  } ${className}`;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const emptyCardsLenght = useMemo(() => itemsData.filter((item) => item.noData).length, [
    itemsData,
    emptyCards
  ]);

  return (
    <Box
      direction={direction}
      className={`${componentClass} item-count-${itemsSize}`}
      width="100"
      style={style}
      dataTestId="carousel"
      focusReferences={(ref) => {
        slickCarouselContainerRef.current = ref;
      }}
    >
      <Slider
        {...settings}
        onSwipe={(direction) => {
          tracking({ type: `CAROUSEL_SWIPE_${direction.toUpperCase()}` });
        }}
        ref={(ref) => {
          slickCarouselRef.current = ref;
        }}
      >
        {itemsData.map((item, index) => {
          const data = { ...item };
          const key = data.index ? data.index : index;
          data.navigationInfo =
            direction === 'ltr'
              ? `${index + 1}/${
                  emptyCards !== elementsToShow ? itemsSize - emptyCardsLenght : itemsSize
                }`
              : `${emptyCards !== elementsToShow ? itemsSize - emptyCardsLenght : itemsSize}/${
                  index + 1
                }`;
          const isVisible = currentIndex <= index && currentIndex + elementsToShow > index;
          // Needed to hide the line separator in the first carousel item in RTL mode
          const firstItemVisibleClass = (index + 1) % elementsToShow === 0 ? 'first-item' : '';
          return (
            <Col height="100" {...colSettings} key={key} onClickCapture={handleOnItemClick}>
              {!item.noData ? (
                <ItemComponentInstance
                  className={firstItemVisibleClass}
                  {...itemSettings}
                  data={data}
                  tabIndex={isVisible}
                  tabIndexCallToAction={isVisible}
                  focusReferences={(ref) => {
                    arrayRef.current[index] = ref;
                  }}
                  ariaLabel={
                    index === 0
                      ? translate(
                          'cmp-carousel-container-explain',
                          'Carousel. Best fares on flights'
                        )
                      : ''
                  }
                />
              ) : (
                <div> </div>
              )}
            </Col>
          );
        })}
      </Slider>
    </Box>
  );
};

Carousel.propTypes = {
  /**
   * Show dots in the component
   */
  showDots: PropTypes.bool,
  /**
   * show Navigation Buttons
   */
  showNavigation: PropTypes.bool,
  /**
   * autoplay
   */
  autoplay: PropTypes.bool,
  /**
   * Infinitely wrap around contents
   */
  infinite: PropTypes.bool,
  /**
   * Prevents autoplay while hovering on dots
   */
  pauseOnDotsHover: PropTypes.bool,
  /**
   * Prevents autoplay while focused on slides
   */
  pauseOnFocus: PropTypes.bool,
  /**
   * Prevents autoplay while hovering on track
   */
  pauseOnHover: PropTypes.bool,
  /**
   * How many slides to show in one frame
   */
  elementsToShow: PropTypes.number,
  /**
   * How many slides to scroll at once
   */
  elementsToScroll: PropTypes.number,
  /**
   * Animation speed in milliseconds
   */
  speed: PropTypes.number,
  /**
   * itemSettings object of settings
   */
  itemSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * prevButtonSettings object of settings
   */
  prevButtonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * nextButtonSettings object of settings
   */
  nextButtonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * itemSettings object of settings
   */
  dotSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component
   */
  ItemComponentInstance: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * List items
   */
  itemsData: PropTypes.arrayOf(PropTypes.any),
  /**
   * Function fire when carousel change
   */
  afterChange: PropTypes.func,
  /**
   * Function fire when carousel before change
   */
  beforeChange: PropTypes.func,
  /**
   * Define if show or hide < > buttons when are disabled
   */
  showArrowsWhenDisabled: PropTypes.bool,
  /**
   * Col settings
   */
  colSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Number of dots to show before reel
   */
  maxNumberDots: PropTypes.number,
  /**
   * Reel component settings
   */
  reelSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Enable or disable keyboard navigation
   */
  enableKeyBoardNavigation: PropTypes.bool,
  /**
   * Ajust the width of card in depedencies of the itemsData.length
   */
  widthCardAuto: PropTypes.bool,
  /**
   * Define height of the cards in the carousel
   */
  cardHeight: PropTypes.string,
  /**
   * Lazy load
   */
  lazyLoad: PropTypes.oneOf(['disabled', 'ondemand', 'progressive']),
  /**
   * Enable padding for scale cards
   */
  scaleCards: PropTypes.bool,
  /**
   * Show uistyle style
   */
  uiStyles: PropTypes.shape({
    borderColor: PropTypes.string,
    borderWidth: PropTypes.string,
    rounded: PropTypes.string
  }),
  /**
   * This setting is to allow
   **/
  preventClickOnDragging: PropTypes.bool,
  /**
   * Function that will handle tracking events
   */
  tracking: PropTypes.func
};

Carousel.defaultProps = {
  showDots: true,
  showNavigation: true,
  autoplay: false,
  infinite: false,
  pauseOnDotsHover: true,
  pauseOnFocus: true,
  pauseOnHover: true,
  elementsToShow: 3,
  elementsToScroll: 3,
  maxNumberDots: 10,
  speed: 500,
  itemSettings: {},
  prevButtonSettings: {
    roundedCircle: true,
    lineHeight: '1',
    paddingWidth: '18px',
    paddingHeight: '15px',
    color: 'dark',
    iconProps: {
      icon: 'left-chevron',
      size: 10
    },
    marginLeft: '-10px'
  },
  nextButtonSettings: {
    roundedCircle: true,
    lineHeight: '1',
    paddingWidth: '18px',
    paddingHeight: '15px',
    color: 'dark',
    iconProps: {
      icon: 'right-chevron',
      size: 10
    },
    marginRight: '-10px'
  },
  reelSettings: {
    textAlign: 'center',
    marginTop: '10px'
  },
  dotSettings: {
    color: 'dark',
    outline: true,
    padding: '4px',
    marginRight: '5px',
    roundedCircle: true,
    dotActiveSetting: {
      outline: false
    },
    sideDots: {
      padding: '2px'
    }
  },
  lazyLoad: 'disabled',
  ItemComponentInstance: null,
  itemsData: [],
  afterChange: () => {},
  beforeChange: () => {},
  showArrowsWhenDisabled: false,
  colSettings: {
    height: '100'
  },
  enableKeyBoardNavigation: false,
  widthCardAuto: true,
  scaleCards: false,
  uiStyles: null,
  cardHeight: null,
  tracking: () => {}
};

Carousel.componentName = 'Carousel';

export default React.memo(
  Carousel,
  (prevProps, nextProps) => prevProps.itemsData === nextProps.itemsData
);
